import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs } from '../lib/money.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import { exportPdfAndReveal, pdfSavesAFile, reportToCsv, downloadCsv, reportFilename } from '../lib/export.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const KINDS = [
  ['daily', 'Today', 'What the till took today'],
  ['weekly', 'This week', 'Monday to now, which is how the rota runs'],
  ['monthly', 'This month', 'The first to now']
];

/**
 * A manager builds a sales report and sends it up.
 *
 * The report is shown in full before it goes. Sending something you have not read is how a
 * wrong number ends up in front of the person who signs the wages.
 */
export function SendReport({ slug }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [kind, setKind] = useState('daily');
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // How it leaves this screen. Downloading and sending are not alternatives to each other:
  // a manager often wants the file in hand as well as in the owner's inbox.
  const [transport, setTransport] = useState('inbox');
  const paper = useRef(null);

  useEffect(() => {
    setPreview(null); setResult(null);
    b.buildReport(kind).then(setPreview).catch(e => setError(e.data?.error || 'failed'));
  }, [kind, slug]);

  const loadSent = () => api.sentReports().then(d => setSent(d.deliveries)).catch(() => {});
  useEffect(() => { loadSent(); }, []);

  async function send() {
    setBusy(true); setError(null);
    try {
      const r = await b.sendReport(kind, { note: note.trim() || null, transport });
      setResult(r);
      setNote('');
      loadSent();
    } catch (e) {
      setError(e.data?.error === 'nobody_to_send_to'
        ? 'There is nobody to send it to. No active admin or super admin account exists.'
        : 'It did not send.');
    } finally { setBusy(false); }
  }

  const r = preview?.report;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="t-body-m secondary">
        Build a sales report and send it up. It lands in the inbox of every admin and super
        admin, with the figures frozen as they are right now.
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        {KINDS.map(([k, label, blurb]) => (
          <button key={k} onClick={() => setKind(k)}
            style={{
              flex: 1, padding: 14, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
              border: '1px solid ' + (kind === k ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
              background: kind === k ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
              borderRadius: 'var(--radius-lg)'
            }}>
            <div className="t-heading-s">{label}</div>
            <div className="t-body-s muted">{blurb}</div>
          </button>
        ))}
      </div>

      {error && <Banner tone="danger">{error}</Banner>}

      {result && (
        <Banner tone="success" title="Sent">
          {result.delivery.title} went to {result.delivery.recipients.length}{' '}
          {result.delivery.recipients.length === 1 ? 'person' : 'people'}. {result.result.note}
        </Banner>
      )}

      {!r && <div className="t-body-m muted">Adding up {kind === 'daily' ? 'today' : `this ${kind.replace('ly', '')}`}</div>}

      {r && (
        <>
          <div style={card} ref={paper} data-report="paper">
            <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
              {r.title.toUpperCase()}
            </div>
            <div className="fig-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: 'var(--color-border-default)' }}>
              <Fig label="TAKINGS" value={ghs(r.totals.revenue)} big />
              <Fig label="SALES" value={r.totals.sales} />
              <Fig label="AVERAGE SALE" value={ghs(r.totals.average)} />
              <Fig label="COMMISSION" value={ghs(r.totals.commission)} />
            </div>

            {r.byStaff.length > 0 && (
              <>
                <div style={{ padding: '11px 16px', borderTop: '1px solid var(--color-border-default)' }} className="t-label-xs muted">
                  WHAT EACH PERSON DID
                </div>
                {r.byStaff.map((s, i) => {
                  // The lines this person delivered, opened up. The money is the headline and
                  // the services underneath are the answer to what they actually spent the
                  // week doing, which the totals alone never say.
                  const did = (r.byStaffService || []).filter(x => x.staff === s.staff);
                  return (
                    <div key={i} style={{ borderTop: '1px solid var(--color-border-default)' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 16px 6px' }}>
                        <span className="t-body-m" style={{ flex: 1, fontWeight: 600 }}>{s.staff}</span>
                        <span className="t-body-s muted">
                          {s.done ?? s.lines} done
                        </span>
                        <span className="money t-money-s" style={{ width: 110, textAlign: 'right' }}>{ghs(s.revenue)}</span>
                        <span className="money t-money-s muted" style={{ width: 100, textAlign: 'right' }}>{ghs(s.commission || 0)}</span>
                      </div>
                      {did.map((x, j) => (
                        <div key={j} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '3px 16px 3px 32px' }}>
                          <span className="t-body-s secondary" style={{ flex: 1 }}>{x.service}</span>
                          <span className="t-body-s muted" style={{ width: 60, textAlign: 'right' }}>
                            {x.qty > x.sold ? `${x.qty}` : `${x.sold}`}
                          </span>
                          <span className="money t-body-s muted" style={{ width: 100, textAlign: 'right' }}>{ghs(x.revenue)}</span>
                          <span style={{ width: 100 }} />
                        </div>
                      ))}
                      <div style={{ height: 6 }} />
                    </div>
                  );
                })}
              </>
            )}

            {r.totals.revenue === 0 && (
              <div style={{ padding: 24, textAlign: 'center' }} className="t-body-m muted">
                Nothing was taken in this period. You can still send it: an empty day is
                information too.
              </div>
            )}
          </div>

          {/* Take it away as a file. Separate from sending on purpose: a manager often wants
              the file in hand as well as in the owner's inbox, not instead of it. */}
          <div style={{ ...card, padding: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="t-label-xs muted" style={{ flex: 1 }}>TAKE A COPY</span>
            <Button size="m" variant="secondary"
              onClick={() => exportPdfAndReveal(paper.current, r.title)}>{pdfSavesAFile() ? 'Download as PDF' : 'Print or save as PDF'}</Button>
            <Button size="m" variant="secondary"
              onClick={() => downloadCsv(reportFilename(r), reportToCsv(r))}>Export for Excel</Button>
          </div>

          <div style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
            <div className="t-label-xs muted">
              GOING TO {preview.recipients.map(p => p.name).join(', ').toUpperCase() || 'NOBODY'}
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {preview.transports.map(t => {
                const emails = preview.recipients.filter(p => p.email).length;
                const unavailable = t.name === 'email' && !emails;
                return (
                  <label key={t.name} style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, cursor: 'pointer',
                    border: '1px solid ' + (transport === t.name ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                    background: transport === t.name ? 'var(--color-accent-subtle)' : 'transparent',
                    borderRadius: 'var(--radius-md)', opacity: unavailable ? 0.6 : 1
                  }}>
                    <input type="radio" name="transport" checked={transport === t.name}
                      onChange={() => setTransport(t.name)} style={{ marginTop: 3 }} />
                    <span>
                      <span className="t-body-m">{t.label}</span>
                      <span className="t-body-s muted" style={{ display: 'block' }}>
                        {t.name === 'inbox'
                          ? 'Straight into their EPMS inbox. Works with the connection down.'
                          : unavailable
                            ? 'Nobody who receives this has an email address on their account.'
                            : `To ${emails} address${emails === 1 ? '' : 'es'}. No mail provider is configured yet, so it waits in the inbox until one is.`}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <Field id="note" label="ANYTHING TO SAY WITH IT" value={note}
              hint="Optional. It shows above the figures when they open it."
              onChange={e => setNote(e.target.value)} />
            <Button disabled={busy || !preview.recipients.length} onClick={send}>
              {busy ? 'Sending' : `Send the ${kind} report`}
            </Button>
          </div>
        </>
      )}

      {sent.length > 0 && (
        <div style={card}>
          <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
            WHAT YOU HAVE SENT
          </div>
          {sent.map(s => (
            <div key={s.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 16px', borderTop: '1px solid var(--color-border-default)' }}>
              <span className="t-body-m" style={{ flex: 1 }}>{s.title}</span>
              <span className="t-body-s muted">
                {new Date(s.sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
              <Chip tone={s.read_count === s.recipients ? 'success' : 'neutral'}>
                read by {s.read_count} of {s.recipients}
              </Chip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Fig = ({ label, value, big }) => (
  <div style={{ padding: 16, background: 'var(--color-bg-surface)' }}>
    <div className="t-label-xs muted">{label}</div>
    <div className={big ? 'money t-money-l' : 'money t-money-m'} style={{ marginTop: 2 }}>{value}</div>
  </div>
);
